import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import { validateJSON, validateJSONNow } from "../web/validation";
import { Entity } from "./entity";
import {CustomFieldDefDataType, toCustomFieldDefDataType} from "./custom-field-def-data-type";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";

export const CustomFieldDefCodec = t.intersection([
  t.type({
    id: t.string,
    companyId: t.string,
    forObjectType: t.string,
    code: t.string,
    dataType: t.string,
    required: t.boolean,
    label: t.string,
  }),
  t.partial({
    refObjectType: t.string,
    createdById: t.string,
    lastModifiedById: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,

    companyName: t.string,
  }),
]);

export type CustomFieldDef = t.TypeOf<typeof CustomFieldDefCodec>;

@Exclude()
export class CustomFieldDefEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  companyId!: string;

  @Expose()
  forObjectType!: string;

  @Expose()
  code!: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toCustomFieldDefDataType))
  dataType!: CustomFieldDefDataType;

  @Expose()
  required!: boolean;

  @Expose()
  label!: string;

  // ─────────────────────────────
  // Optional fields
  // ─────────────────────────────

  @Expose()
  refObjectType?: string;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  companyName?: string;

  readonly entityType = "CUSTOMFIELDDEF";

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for CustomFieldDefEntity. Recommend using the {@link from} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: CustomFieldDef): CustomFieldDefEntity {
    return plainToInstance(CustomFieldDefEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<CustomFieldDef> {
    const object = instanceToPlain(this, { excludeExtraneousValues: true });
    return validateJSON(CustomFieldDefCodec, object);
  }

  asTypeValue(): CustomFieldDef {
    const object = instanceToPlain(this, { excludeExtraneousValues: true });
    return validateJSONNow(CustomFieldDefCodec, object);
  }
}

export const humanizeDefCode = (defCode?: string | null): string => {
  if (!defCode || defCode.trim().length === 0) {
    return "Custom Field";
  }

  const s = defCode
    .trim()
    .replace(/-/g, " ")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2");

  return s.replace(/^./, (c) => c.toUpperCase());
}
